import React, { useState, useEffect, useRef } from "react";
import { Headline, Paragraph, Section, Button } from "./ui";
import { getFlatVariantData } from "../utils/csvParser";
import "./InspectionPricingSearch.css";

const InspectionPricingSearch = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchIndex, setSearchIndex] = useState([]);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Initialize search index on component mount
  useEffect(() => {
    const loadSearchIndex = async () => {
      try {
        const csvData = await getFlatVariantData();
        const index = csvData.map((row, index) => ({
          id: index,
          model: row["Model"],
          generation: row["Generation"],
          variant: row["Variant"],
          engine: row["Engine"],
          power: row["Power"],
          years: row["Years"],
          fullPath: `${row["Model"]} ${row["Generation"]} ${row["Variant"]}`,
          searchTerms: row["Search Terms"]
            ? row["Search Terms"].split("; ")
            : [],
          pricing: {
            kleine: {
              min: row["Kleine Inspektion Min"],
            },
            grosse: {
              min: row["Grosse Inspektion Min"],
            },
          },
          notes: row["Notes"] ? row["Notes"].split(", ") : [],
        }));
        setSearchIndex(index);
      } catch (error) {
        console.error("Error loading search index:", error);
        setSearchIndex([]);
      }
    };

    loadSearchIndex();
  }, []);

  // Helper function to strip "porsche" from search term
  const stripPorsche = (searchTerm) => {
    return searchTerm
      .toLowerCase()
      .replace(/\bporsche\b/g, "") // Remove "porsche" word
      .replace(/\s+/g, " ") // Replace multiple spaces with single space
      .trim(); // Remove leading/trailing spaces
  };

  // Handle search input changes
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length >= 2) {
      // Strip "porsche" from the search term before filtering
      const cleanedSearchTerm = stripPorsche(value);

      // Only search if there's still content after stripping "porsche"
      if (cleanedSearchTerm.length >= 1) {
        const filtered = searchIndex.filter((item) => {
          const searchLower = cleanedSearchTerm;
          return (
            item.searchTerms.some((term) =>
              term.toLowerCase().includes(searchLower)
            ) ||
            item.variant.toLowerCase().includes(searchLower) ||
            item.model.toLowerCase().includes(searchLower) ||
            item.generation.toLowerCase().includes(searchLower)
          );
        });
        setSuggestions(filtered.slice(0, 3)); // Limit to 3 suggestions
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    setSelectedVariant(suggestion);
    setSearchTerm(suggestion.fullPath);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle input focus
  const handleInputFocus = () => {
    // Clear the search input when user clicks on it
    setSearchTerm("");
    setSelectedVariant(null);
    setSuggestions([]);
    setShowSuggestions(false);

    // Scroll the search box to the top of the viewport
    if (inputRef.current) {
      inputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Handle input blur (with delay to allow suggestion clicks)
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  // Clear search
  const handleClear = () => {
    setSearchTerm("");
    setSelectedVariant(null);
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <Section variant="primary" spacing="none_top_compact_bottom" margin={false}>
      {/* Search Input */}
      <div className="relative mb-8">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder="Porsche-Modell suchen (z.B. 997, Boxster S, GT3)..."
            className="inspection-search-input w-full px-4 py-3 border-2 rounded-lg text-lg transition-all"
          />
          {searchTerm && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xl opacity-60 hover:opacity-100 transition-opacity"
              style={{ color: "var(--color-secondary)" }}
            >
              ×
            </button>
          )}
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-140 overflow-y-auto"
            style={{
              backgroundColor: "var(--color-primary)",
              borderColor: "var(--color-secondary)",
            }}
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.id}
                onClick={() => handleSuggestionClick(suggestion)}
                className="px-4 py-4 cursor-pointer hover:opacity-90 transition-opacity border-b last:border-b-0"
                style={{
                  borderColor: "var(--color-secondary)",
                  opacity: 0.95,
                }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <Paragraph
                      variant="medium"
                      color="secondary"
                      className="font-medium"
                    >
                      {suggestion.variant}
                    </Paragraph>
                    <Paragraph
                      variant="small"
                      color="secondary"
                      className="opacity-75"
                    >
                      {suggestion.model} • {suggestion.generation} •{" "}
                      {suggestion.years} • {suggestion.engine} •{" "}
                      {suggestion.power}
                    </Paragraph>
                  </div>
                  <div className="ml-4 text-right">
                    <Paragraph
                      variant="small"
                      color="secondary"
                      className="opacity-75"
                    >
                      ab €{suggestion.pricing.kleine.min}
                    </Paragraph>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Variant Details */}
      {selectedVariant && (
        <div
          className="p-6 rounded-lg border"
          style={{
            backgroundColor: "var(--color-primary)",
            borderColor: "var(--color-secondary)",
            opacity: 0.95,
          }}
        >
          <div className="mb-4">
            <Headline variant="h3" color="secondary" className="font-medium">
              {selectedVariant.variant}
            </Headline>
            <Paragraph
              variant="medium"
              color="secondary"
              className="opacity-75"
            >
              {selectedVariant.model} • {selectedVariant.generation} •{" "}
              {selectedVariant.years}
            </Paragraph>
          </div>

          <div className="mb-6">
            {/* Pricing */}
            <div>
              <Headline
                variant="h4"
                color="secondary"
                className="font-medium mb-3"
              >
                Inspektionspreise
              </Headline>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Paragraph
                    variant="small"
                    color="secondary"
                    className="opacity-75"
                  >
                    Kleine Inspektion:
                  </Paragraph>
                  <Paragraph
                    variant="small"
                    color="secondary"
                    className="font-medium"
                  >
                    ab €{selectedVariant.pricing.kleine.min}
                  </Paragraph>
                </div>
                <div className="flex justify-between">
                  <Paragraph
                    variant="small"
                    color="secondary"
                    className="opacity-75"
                  >
                    Große Inspektion:
                  </Paragraph>
                  <Paragraph
                    variant="small"
                    color="secondary"
                    className="font-medium"
                  >
                    ab €{selectedVariant.pricing.grosse.min}
                  </Paragraph>
                </div>
              </div>

              {/* WhatsApp Booking Button */}
              <div className="mt-4">
                <Button
                  as="a"
                  href={`https://wa.me/4915756850714?text=${encodeURIComponent(
                    `Ich interessiere mich für eine Inspektion für einen ${selectedVariant.variant}.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="secondary"
                  size="medium"
                  className="w-full"
                >
                  Jetzt Termin per WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Results Message */}
      {searchTerm.length >= 2 &&
        suggestions.length === 0 &&
        !showSuggestions &&
        !selectedVariant && (
          <div className="text-center py-8">
            <Paragraph
              variant="medium"
              color="secondary"
              className="opacity-75"
            >
              Keine Ergebnisse für "{searchTerm}" gefunden.
            </Paragraph>
            <Paragraph
              variant="small"
              color="secondary"
              className="opacity-60 mt-2"
            >
              Versuchen Sie es mit einem anderen Suchbegriff wie "997",
              "Boxster" oder "GT3".
            </Paragraph>
            {searchTerm.toLowerCase().includes("porsche") && (
              <Paragraph
                variant="small"
                color="secondary"
                className="opacity-60 mt-2"
              >
                Hinweis: "Porsche" wird automatisch aus der Suche entfernt.
              </Paragraph>
            )}
          </div>
        )}
    </Section>
  );
};

export default InspectionPricingSearch;
